// ABOUTME: Document-level undo/redo stack for report editor.
// ABOUTME: Captures section snapshots with debounced batching for text edits and immediate capture for structural changes.

import { useRef, useState, useCallback, useEffect } from 'react';
import type { ReportSection } from '@/types/api';

const MAX_STACK_SIZE = 50;
const DEBOUNCE_MS = 1000;

/**
 * Document-level undo/redo for the report editor.
 *
 * Two capture modes:
 * - `captureBeforeTextEdit(sections)`: debounced — captures the "before" state on
 *   the first call, then ignores subsequent calls until 1 second of inactivity.
 * - `captureBeforeStructuralChange(sections)`: immediate — for add/delete/reorder.
 *
 * Undo/redo take the current sections and return the restored state (or null).
 */
export function useDocumentHistory() {
  const pastRef = useRef<ReportSection[][]>([]);
  const futureRef = useRef<ReportSection[][]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSerializedRef = useRef<string>('');
  // Trigger re-render so canUndo/canRedo stay fresh
  const [, setTick] = useState(0);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const push = useCallback((sections: ReportSection[]) => {
    const serialized = JSON.stringify(sections);
    if (serialized === lastSerializedRef.current) return; // skip duplicates
    pastRef.current.push(JSON.parse(serialized));
    if (pastRef.current.length > MAX_STACK_SIZE) pastRef.current.shift();
    futureRef.current = [];
    lastSerializedRef.current = serialized;
    setTick((t) => t + 1);
  }, []);

  /**
   * Call BEFORE a text edit is applied.
   * On the first call after idle, captures the current state.
   * Subsequent calls within DEBOUNCE_MS just extend the batch window.
   */
  const captureBeforeTextEdit = useCallback(
    (sections: ReportSection[]) => {
      if (debounceRef.current) {
        // Already in a batch — just extend the timer
        clearTimeout(debounceRef.current);
      } else {
        // First change in a new batch — capture the "before" state
        push(sections);
      }
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
      }, DEBOUNCE_MS);
    },
    [push],
  );

  /**
   * Call BEFORE a structural change (add, delete, reorder, AI edit apply).
   * Immediately captures, flushing any pending text-edit batch.
   */
  const captureBeforeStructuralChange = useCallback(
    (sections: ReportSection[]) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      push(sections);
    },
    [push],
  );

  const undo = useCallback(
    (currentSections: ReportSection[]): ReportSection[] | null => {
      // Flush pending debounce
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (pastRef.current.length === 0) return null;
      futureRef.current.push(structuredClone(currentSections));
      const prev = pastRef.current.pop()!;
      lastSerializedRef.current = JSON.stringify(prev);
      setTick((t) => t + 1);
      return prev;
    },
    [],
  );

  const redo = useCallback(
    (currentSections: ReportSection[]): ReportSection[] | null => {
      if (futureRef.current.length === 0) return null;
      pastRef.current.push(structuredClone(currentSections));
      const next = futureRef.current.pop()!;
      lastSerializedRef.current = JSON.stringify(next);
      setTick((t) => t + 1);
      return next;
    },
    [],
  );

  return {
    captureBeforeTextEdit,
    captureBeforeStructuralChange,
    undo,
    redo,
    canUndo: pastRef.current.length > 0,
    canRedo: futureRef.current.length > 0,
  };
}
